// src/components/document/BacklinksPanel.tsx
// Displays backlinks — documents that reference the current document via [[wikilinks]].

import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import {
  getStatusMapping,
  documentStatusMap,
  formatRelativeTime,
} from '@/features/document/logic';
import type { Backlink } from '@/features/document/types';

interface BacklinksPanelProps {
  backlinks: Backlink[];
  isLoading: boolean;
  onNavigate: (docId: string) => void;
}

export function BacklinksPanel({ backlinks, isLoading, onNavigate }: BacklinksPanelProps) {
  const { t, locale } = useI18n();

  if (isLoading) {
    return (
      <div className="mt-8 border-t border-border pt-6">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
          </svg>
          <h3 className="text-sm font-semibold text-foreground">{t('document.backlinks.title')}</h3>
        </div>
        <div className="space-y-2 animate-pulse">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (backlinks.length === 0) return null;

  return (
    <div className="mt-8 border-t border-border pt-6">
      <div className="flex items-center gap-2 mb-4">
        <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
        </svg>
        <h3 className="text-sm font-semibold text-foreground">{t('document.backlinks.title')}</h3>
        <Badge variant="secondary" size="sm">{backlinks.length}</Badge>
      </div>

      <div className="space-y-2">
        {backlinks.map((bl) => {
          const statusInfo = getStatusMapping(documentStatusMap, bl.sourceStatus);
          return (
            <button
              key={bl.sourceId}
              onClick={() => onNavigate(bl.sourceId)}
              className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted/50 transition-colors group"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
                  {bl.sourceTitle}
                </span>
                <Badge variant={statusInfo.variant as any} size="sm">
                  {t(statusInfo.labelKey)}
                </Badge>
              </div>
              {bl.snippet && (
                <p className="text-xs text-muted-foreground line-clamp-2">{bl.snippet}</p>
              )}
              {bl.updatedAt && (
                <span className="text-[11px] text-muted-foreground/70 mt-1 block">
                  {formatRelativeTime(bl.updatedAt, locale)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
